import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Sprout, Mail, Calendar, Download, CheckCircle2 } from "lucide-react";
import NavBar from "@/components/NavBar";
import Footer from "@/components/Footer";
import { API, api } from "@/lib/api";

export default function BookingThanksPage() {
  const { bookingId } = useParams();
  const [booking, setBooking] = useState(null);

  useEffect(() => {
    let alive = true;
    const load = async () => {
      try {
        const { data } = await api.get(`/bookings/${bookingId}`);
        if (alive) setBooking(data);
      } catch {
        // ignore
      }
    };
    load();
    const t = setInterval(load, 5000);
    return () => {
      alive = false;
      clearInterval(t);
    };
  }, [bookingId]);

  if (!booking) {
    return (
      <>
        <NavBar />
        <div className="max-w-2xl mx-auto p-12">
          <div className="h-60 rounded-2xl border border-gg-border bg-gg-surface animate-pulse" />
        </div>
      </>
    );
  }

  const isConfirmed = booking.status === "confirmed";

  return (
    <>
      <NavBar />
      <main className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16">
        <div className="text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gg-green/15 text-gg-green mb-6">
            {isConfirmed ? <CheckCircle2 size={32} /> : <Sprout size={32} />}
          </div>
          <p className="font-script text-3xl text-gg-green leading-none">
            {isConfirmed ? "you're booked" : "request received"}
          </p>
          <h1 className="font-heading text-4xl sm:text-5xl mt-2 text-[var(--gg-text)] leading-tight">
            {isConfirmed
              ? `Confirmed for ${booking.student_name}`
              : `Thanks, ${booking.parent_name}`}
          </h1>
          <p className="mt-4 text-gg-muted">
            One-on-One Garden Tutoring with Heather Mich
          </p>
        </div>

        {isConfirmed ? (
          <ConfirmedBlock booking={booking} />
        ) : (
          <PendingBlock booking={booking} />
        )}

        {/* Referral */}
        <div className="mt-8 rounded-2xl border border-dashed border-gg-green bg-gg-green/8 p-6 sm:p-7">
          <p className="font-script text-2xl text-gg-green leading-none mb-1">bring a friend</p>
          <h3 className="font-heading text-2xl text-[var(--gg-text)] leading-tight">
            Save $10 — and give your friend $10 too.
          </h3>
          <p className="mt-3 text-sm text-gg-muted leading-relaxed max-w-xl">
            Know another ESA family who'd love a 1-on-1 with Heather? When they book and mention{" "}
            <strong className="text-[var(--gg-text)]">{booking.parent_name?.split(" ")[0]}</strong> in their notes,
            you'll both get a <strong className="text-[var(--gg-text)]">$10 credit</strong> on your next session.
          </p>
        </div>

        <div className="mt-10 text-center">
          <Link to="/" className="btn-pill btn-outline inline-flex">
            Back to homepage
          </Link>
        </div>
      </main>
      <Footer />
    </>
  );
}

function PendingBlock({ booking }) {
  return (
    <>
      <div className="mt-10 rounded-2xl border-2 border-gg-green bg-gg-green/8 p-6 sm:p-8 text-center">
        <p className="text-xs uppercase tracking-[0.22em] text-gg-brown font-semibold">
          What happens next
        </p>
        <h2 className="font-heading text-2xl sm:text-3xl text-[var(--gg-text)] mt-3 leading-tight">
          Heather will reach out within 1–2 days to confirm a time.
        </h2>
        <p className="mt-3 text-gg-muted max-w-xl mx-auto">
          Once she confirms one of your suggested slots, you'll receive your ESA invoice here (this page updates automatically) and a calendar invite at {booking.parent_email}.
        </p>
      </div>

      <div className="mt-8 rounded-2xl border border-gg-border bg-gg-surface p-6">
        <p className="text-xs uppercase tracking-[0.18em] text-gg-brown font-semibold mb-3">
          Your suggested times
        </p>
        <ul className="space-y-2">
          {(booking.preferred_slots || []).map((s, i) => (
            <li key={i} className="flex items-center gap-3 text-sm">
              <Calendar size={14} className="text-gg-green" /> {s.label}
            </li>
          ))}
        </ul>
      </div>

      <div className="mt-6 rounded-2xl border border-gg-border bg-gg-bg p-5 text-sm text-gg-muted flex items-start gap-3">
        <Mail size={16} className="text-gg-brown mt-0.5" />
        <span>
          Need to reach Heather sooner? Email{" "}
          <a className="text-gg-green font-semibold" href="mailto:Heather@gracefulgardencoaching.com">
            Heather@gracefulgardencoaching.com
          </a>{" "}
          or call (805) 444-6144.
        </span>
      </div>
    </>
  );
}

function ConfirmedBlock({ booking }) {
  const invoiceUrl = booking.invoice_token
    ? `${API}/bookings/${booking.id}/invoice.pdf?t=${booking.invoice_token}`
    : `${API}/bookings/${booking.id}/invoice.pdf`;
  const confirmed = new Date(booking.confirmed_start_iso).toLocaleString("en-US", {
    weekday: "long",
    month: "long",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
    timeZone: "America/Phoenix",
  });
  return (
    <>
      <div className="mt-10 rounded-2xl border-2 border-gg-green bg-gg-green/8 p-6 sm:p-8 text-center">
        <p className="text-xs uppercase tracking-[0.22em] text-gg-brown font-semibold">
          Confirmed time
        </p>
        <h2 className="font-heading text-3xl sm:text-4xl text-[var(--gg-text)] mt-3 leading-tight">
          {confirmed} <span className="text-gg-muted text-base">(Arizona time)</span>
        </h2>
        {booking.calendar_event_link && (
          <a
            href={booking.calendar_event_link}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-2 mt-4 text-sm text-gg-green hover:underline"
          >
            <Calendar size={14} /> View on Google Calendar
          </a>
        )}
      </div>

      <div className="mt-8 rounded-2xl border-2 border-gg-brown bg-gg-surface p-6 sm:p-8 text-center">
        <p className="text-xs uppercase tracking-[0.22em] text-gg-brown font-semibold">
          ESA / ClassWallet
        </p>
        <h2 className="font-heading text-2xl sm:text-3xl text-[var(--gg-text)] mt-3">
          Download your ClassWallet invoice
        </h2>
        <p className="text-gg-muted mt-2 max-w-xl mx-auto">
          Upload this PDF in your ClassWallet portal under "Pay Vendor → Graceful Garden Coaching" within 7 days.
        </p>
        <a
          href={invoiceUrl}
          target="_blank"
          rel="noreferrer"
          className="btn-pill btn-primary mt-6 inline-flex text-base"
        >
          <Download size={18} /> Download Invoice #{booking.invoice_number}
        </a>
      </div>
    </>
  );
}
